import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
} from "@angular/core";
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from "@angular/forms";
// eslint-disable-next-line @typescript-eslint/no-var-requires
const n2words = require("n2words").default;
import { NgbModal, NgbModalRef } from "@ng-bootstrap/ng-bootstrap";
import { TranslateService } from "@ngx-translate/core";
import { finalize } from "rxjs/operators";
import Swal from "sweetalert2";

import { Subscription } from "rxjs";
import { EventService } from "src/app/core/services/event.service";
import { PermissionsService } from "src/app/core/services/permissions.service";
import {
  CmiAvailabilityResponse,
  CmiFormSchemaField,
  CmiFormSchemaGroup,
  CmiFormSchemaRequest,
  CmiFormSchemaResponse,
  CmiGeneratedDocumentSummary,
  FilesService,
} from "src/app/core/services/files.service";
import { PropertyDataService } from "src/app/core/services/property-data.service";
import { PropertyValidationRulesService } from "src/app/core/services/propertyValidationRules.service";
import {
  extractMissingFields,
  missingFieldsHtml,
} from "src/app/core/utils/missing-fields-alert.util";
import { Deals } from "src/app/pages/deals/models/deals.model";

interface DynamicCmiField extends CmiFormSchemaField {
  controlName: string;
}

interface DynamicCmiGroup extends Omit<CmiFormSchemaGroup, "fields"> {
  panelId: string;
  fields: DynamicCmiField[];
}

interface CmiErrorBody {
  code?: string;
  message?: string | string[];
  translationKey?: string;
  /** 422 `PROPERTY_REQUIRED_FIELDS_INCOMPLETE`: os campos da ficha em falta. */
  missingFields?: any[];
}

/** Campos monetários e o campo "por extenso" que cada um alimenta. */
const AMOUNT_IN_WORDS_PAIRS: Array<[string, string]> = [
  ["PROPERTY.PRICE", "PROPERTY.PRICEWRITTEN"],
  ["CONTRACT.DOCUMENTSVALUE", "CONTRACT.DOCUMENTSVALUEWRITTEN"],
];

/** Valor monetário por extenso em português, como no formulário anterior. */
function amountInWords(value: unknown): string {
  const amount = Number(value);
  if (!Number.isFinite(amount) || amount <= 0) return "";
  const units = Math.floor(amount);
  const cents = Math.round((amount - units) * 100);

  const text =
    `${n2words(units, { lang: "pt-PT" })} euro${units === 1 ? "" : "s"}` +
    (cents > 0
      ? ` e ${n2words(cents, { lang: "pt-PT" })} cêntimo${cents === 1 ? "" : "s"}`
      : "");
  return text.charAt(0).toUpperCase() + text.slice(1);
}

@Component({
  selector: "app-mediation-contract",
  templateUrl: "./mediation-contract.component.html",
  styleUrls: ["./mediation-contract.component.scss"],
})
export class MediationContractComponent implements OnInit, OnDestroy {
  @Input() property: { id?: string } | null = null;
  @Input() deal: Deals | null = null;
  /** Kept for call-site compatibility; the schema decides the current values. */
  @Input() isEdit = false;
  @Input() isProperty = false;
  /** Loja escolhida no separador de detalhes, para a sonda do template. */
  @Input() storeId: number | null = null;
  /**
   * O imóvel ainda tem referência temporária: gerar o CMI atribui a
   * definitiva, por isso pede-se confirmação antes e avisa-se o pai depois.
   */
  @Input() isTempReference = false;
  /** Gerou-se o CMI num imóvel com referência temporária — a referência mudou. */
  @Output() referenceAssigned = new EventEmitter<void>();

  @ViewChild("mediationContractModal") mediationContractModal?: TemplateRef<unknown>;

  dynamicForm: FormGroup;
  schema?: CmiFormSchemaResponse;
  groups: DynamicCmiGroup[] = [];
  activePanelIds: string[] = [];
  loadingSchema = false;
  spinnerLoading = false;
  schemaLoadFailed = false;
  formSubmitted = false;

  /** Estado da aba de mediação do imóvel. */
  summaryForm: FormGroup;
  availability?: CmiAvailabilityResponse;
  loadingAvailability = false;
  entityHasTemplate = false;
  readonly durationOptions = [3, 6, 12, 24, 36];

  private modalRef?: NgbModalRef;
  private amountSubscriptions = new Subscription();
  private readonly subscriptions = new Subscription();
  /** `openGeneratorWhenReady`: abrir o gerador assim que a disponibilidade chegar. */
  private openGeneratorPending = false;

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly modalService: NgbModal,
    private readonly filesService: FilesService,
    private readonly eventService: EventService,
    private readonly translate: TranslateService,
    private readonly propertyData: PropertyDataService,
    private readonly permissionsService: PermissionsService,
    private readonly propertyValidationService: PropertyValidationRulesService,
  ) {
    this.dynamicForm = this.formBuilder.group({});
    this.summaryForm = this.formBuilder.group({
      contractNumber: [null],
      startDateMediation: [null],
      endDateMediation: [null],
      defineEndDate: [null],
      autoRenewal: [false],
    });
  }

  ngOnInit(): void {
    if (this.isProperty) {
      this.initSummaryForm();
      this.loadAvailability();
      this.checkTemplateForUnsavedProperty();
      return;
    }
    this.loadSchema();
  }

  ngOnDestroy(): void {
    this.amountSubscriptions.unsubscribe();
    this.subscriptions.unsubscribe();
    this.modalRef?.dismiss("component-destroyed");
  }

  get documents(): CmiGeneratedDocumentSummary[] {
    return this.availability?.documents || [];
  }

  /** Quem pode gerar tem template disponível E a permissão CAN_GENERATE_CMI. */
  get canGenerate(): boolean {
    return (
      this.availability?.available === true &&
      this.permissionsService.hasPermission("CAN_GENERATE_CMI")
    );
  }

  get hasContext(): boolean {
    const request = this.contextRequest();
    return Boolean(request.propertyId || request.dealId);
  }

  /**
   * O número de CMI e o prazo continuam a ser guardados com o imóvel, através
   * do PropertyDataService — é o mesmo canal que o formulário antigo usava.
   */
  private initSummaryForm(): void {
    this.applyValidationRuleWarnings();

    const current = this.propertyData.currentCmiValue || {};
    this.summaryForm.patchValue(
      {
        contractNumber: current.contractNumber ?? null,
        startDateMediation: current.startDateMediation ?? null,
        endDateMediation: current.endDateMediation ?? null,
        defineEndDate: current.defineEndDate ?? null,
        autoRenewal: current.autoRenewal ?? false,
      },
      { emitEvent: false },
    );
    this.subscriptions.add(
      this.summaryForm.valueChanges.subscribe((value) =>
        this.propertyData.seedFromCMI(value),
      ),
    );
  }

  /**
   * Avisos amarelos nos campos que o MASTER marcou como obrigatórios para
   * ativar o imóvel (chaves `cmi.*` do catálogo de requisitos).
   *
   * O `warningIfEnabled` só avisa com a regra ligada, por isso pode ser posto
   * sempre. As regras chegam por HTTP e podem ainda não estar carregadas quando
   * o separador abre — daí voltar a validar quando o mapa chega.
   */
  private applyValidationRuleWarnings(): void {
    const warn = (ruleKey: string) =>
      this.propertyValidationService.warningIfEnabled(
        ruleKey,
        (control) => !control?.value,
      );

    this.summaryForm
      .get("contractNumber")
      ?.setValidators([warn("cmi.contractNumber")]);
    this.summaryForm
      .get("startDateMediation")
      ?.setValidators([warn("cmi.startDateMediation")]);
    this.summaryForm
      .get("endDateMediation")
      ?.setValidators([warn("cmi.endDateMediation")]);

    this.summaryForm.updateValueAndValidity({ emitEvent: false });

    this.subscriptions.add(
      this.propertyValidationService.rules$().subscribe((map) => {
        if (!map?.size) return;

        Object.values(this.summaryForm.controls).forEach((control) =>
          control.updateValueAndValidity({ emitEvent: false }),
        );
      }),
    );
  }

  /**
   * Aviso amarelo por campo, no mesmo padrão dos restantes separadores: fica à
   * vista enquanto o campo estiver por preencher, para indicar o que falta —
   * não espera por uma tentativa de gravação.
   */
  hasWarning(controlName: string): boolean {
    return !!this.summaryForm.get(controlName)?.errors?.["warning"];
  }

  /** Com um prazo escolhido, a data de fim passa a ser derivada do início. */
  onDurationChange(): void {
    const months = Number(this.summaryForm.get("defineEndDate")?.value);
    const start = this.summaryForm.get("startDateMediation")?.value;
    if (!months || !start) return;
    const end = new Date(start);
    if (Number.isNaN(end.getTime())) return;
    end.setMonth(end.getMonth() + months);
    this.summaryForm
      .get("endDateMediation")
      ?.setValue(end.toISOString().slice(0, 10));
  }

  get endDateLocked(): boolean {
    return Number(this.summaryForm.get("defineEndDate")?.value) > 0;
  }

  /**
   * Num imóvel por gravar não há contexto para o `availability`, por isso o
   * separador não sabia sequer se valia a pena dizer "guarde primeiro". Esta
   * sonda responde a isso sem imóvel — e continua a não mostrar nada a quem
   * não tem CMI configurado.
   */
  private checkTemplateForUnsavedProperty(): void {
    if (this.hasContext || !this.permissionsService.hasPermission("CAN_GENERATE_CMI")) {
      return;
    }

    this.subscriptions.add(
      this.filesService.getCmiTemplateAvailability(this.storeId ?? undefined)
        .subscribe({
          next: ({ available }) => (this.entityHasTemplate = !!available),
          error: () => (this.entityHasTemplate = false),
        }),
    );
  }

  /** Imóvel por gravar, com CMI configurado: só falta gravar para poder gerar. */
  get showSavePropertyFirst(): boolean {
    return this.isProperty && !this.hasContext && this.entityHasTemplate;
  }

  loadAvailability(): void {
    if (this.loadingAvailability) return;
    // Um imóvel ainda por gravar não tem contexto para resolver template.
    if (!this.hasContext) {
      this.availability = undefined;
      this.openGeneratorPending = false;
      return;
    }
    this.loadingAvailability = true;
    this.filesService
      .getCmiAvailability(this.contextRequest())
      .pipe(
        finalize(() => {
          this.loadingAvailability = false;
          this.flushPendingGenerator();
        }),
      )
      .subscribe({
        next: (availability) => (this.availability = availability),
        // Sem template ou sem contexto a aba continua utilizável: apenas não
        // aparece a opção de gerar.
        error: () => (this.availability = undefined),
      });
  }

  /**
   * Abre o gerador assim que a disponibilidade estiver resolvida — para o pai
   * (ficha do imóvel, depois de gerar a referência) encadear "gerar o CMI
   * agora" sem depender de o separador já ter sondado o template.
   */
  openGeneratorWhenReady(): void {
    this.openGeneratorPending = true;
    // Pedido em curso: o `finalize` dele abre o gerador. Senão pede-se agora
    // (sem contexto `loadAvailability` limpa o pendente e não abre nada).
    if (this.loadingAvailability) return;
    this.loadAvailability();
  }

  private flushPendingGenerator(): void {
    if (!this.openGeneratorPending) return;
    this.openGeneratorPending = false;
    if (this.canGenerate) this.openGenerator();
  }

  /** Só a partir daqui é que o formulário dinâmico do CMI é carregado. */
  openGenerator(): void {
    if (!this.canGenerate || this.loadingSchema) return;
    this.loadingSchema = true;
    this.schemaLoadFailed = false;
    this.filesService
      .getCmiFormSchema(this.contextRequest())
      .pipe(finalize(() => (this.loadingSchema = false)))
      .subscribe({
        next: (schema) => {
          this.applySchema(schema);
          this.openModal();
        },
        error: (error) => {
          this.schemaLoadFailed = true;
          void this.showRuntimeError(
            error,
            "t-cmi-templates.runtime.errors.schema-load",
          );
        },
      });
  }

  downloadDocument(document: CmiGeneratedDocumentSummary): void {
    this.filesService.downloadGeneratedCmiDocument(String(document.id)).subscribe({
      next: (response) => {
        if (!response.body) return;
        this.downloadPdf(
          response.body,
          this.fileNameFromDisposition(
            response.headers.get("Content-Disposition"),
            document.fileName || "cmi.pdf",
          ),
        );
      },
      error: (error) =>
        void this.showRuntimeError(
          error,
          "t-cmi-templates.runtime.errors.download",
        ),
    });
  }

  trackDocument(_: number, document: CmiGeneratedDocumentSummary): string {
    return String(document.id);
  }

  loadSchema(): void {
    if (this.loadingSchema) return;
    this.loadingSchema = true;
    this.schemaLoadFailed = false;
    this.filesService
      .getCmiFormSchema(this.contextRequest())
      .pipe(finalize(() => (this.loadingSchema = false)))
      .subscribe({
        next: (schema) => {
          this.applySchema(schema);
          if (!this.isProperty) this.openModal();
        },
        error: (error) => {
          this.schemaLoadFailed = true;
          if (!this.isProperty) this.openModal();
          void this.showRuntimeError(
            error,
            "t-cmi-templates.runtime.errors.schema-load",
          );
        },
      });
  }

  async submitContract(): Promise<void> {
    if (!this.canGenerate) return;
    this.formSubmitted = true;
    this.dynamicForm.markAllAsTouched();
    if (!this.schema || this.dynamicForm.invalid || this.spinnerLoading) return;

    // Com referência temporária, gerar o CMI atribui a definitiva — o
    // utilizador tem de saber antes. Sem referência temporária nada muda.
    if (this.isTempReference) {
      const confirmed = await Swal.fire({
        title: this.translate.instant("t-listing-detail-cmi-generate-confirm"),
        text:
          `${this.translate.instant("t-listing-detail-cmi-generate-confirm-text")} ` +
          this.translate.instant("t-cmi-generate-temp-reference-text"),
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "rgb(54, 69, 116)",
        cancelButtonColor: "#74788d",
        confirmButtonText: this.translate.instant("t-yes"),
        cancelButtonText: this.translate.instant("t-cancel"),
      });
      if (!confirmed.isConfirmed) return;
    }
    if (this.spinnerLoading) return;

    this.spinnerLoading = true;
    this.filesService
      .generateCmiDocument({
        ...this.generationContext(),
        expectedTemplateVersionId: this.schema.templateVersionId,
        values: this.formValues(),
      })
      .pipe(finalize(() => (this.spinnerLoading = false)))
      .subscribe({
        next: (response) => {
          if (!response.body) {
            void this.showRuntimeError(
              undefined,
              "t-cmi-templates.runtime.errors.generate",
            );
            return;
          }
          const fallbackName = `cmi-${this.deal?.id || this.property?.id || "documento"}.pdf`;
          this.downloadPdf(
            response.body,
            this.fileNameFromDisposition(
              response.headers.get("Content-Disposition"),
              fallbackName,
            ),
          );
          if (this.modalRef) {
            this.modalRef.close("generated");
          } else {
            this.eventService.broadcast("contract-generated");
          }
          // O CMI acabado de gerar tem de aparecer já na lista do imóvel.
          if (this.isProperty) this.loadAvailability();
          // A referência definitiva foi atribuída ao gerar: o pai recarrega
          // a ficha (o número de contrato e a referência mudaram).
          if (this.isProperty && this.isTempReference) {
            this.referenceAssigned.emit();
          }
        },
        error: (error) => void this.handleGenerationError(error),
      });
  }

  isInvalid(field: DynamicCmiField): boolean {
    const control = this.dynamicForm.get(field.controlName);
    return !!control && control.invalid && (control.touched || this.formSubmitted);
  }

  trackGroup(_: number, group: DynamicCmiGroup): string {
    return group.panelId;
  }

  trackField(_: number, field: DynamicCmiField): string {
    return field.sourceKey;
  }

  closeModal(): void {
    this.modalRef?.dismiss("cancelled");
  }

  private applySchema(schema: CmiFormSchemaResponse): void {
    const seenSourceKeys = new Set<string>();
    const controls: Record<string, FormControl> = {};
    this.groups = (schema.groups || [])
      .map((group, groupIndex): DynamicCmiGroup => {
        const fields = (group.fields || []).reduce<DynamicCmiField[]>(
          (unique, field) => {
            const sourceKey = String(field.sourceKey || "").trim().toUpperCase();
            if (!sourceKey || seenSourceKeys.has(sourceKey)) return unique;
            seenSourceKeys.add(sourceKey);
            const controlName = `cmiField${seenSourceKeys.size - 1}`;
            const normalized: DynamicCmiField = {
              ...field,
              sourceKey,
              controlName,
              multiline: field.supportsMultiline === true && field.multiline === true,
              required: field.required === true,
            };
            const validators = [
              ...(normalized.required && normalized.valueType !== "CHECKBOX"
                ? [Validators.required]
                : []),
              ...(normalized.maxCharacters
                ? [Validators.maxLength(normalized.maxCharacters)]
                : []),
            ];
            controls[controlName] = new FormControl(
              this.initialValue(normalized),
              validators,
            );
            return [...unique, normalized];
          },
          [],
        );
        return {
          key: group.key,
          labelKey: group.labelKey,
          panelId: `cmi-runtime-group-${groupIndex}`,
          fields,
        };
      })
      .filter((group) => group.fields.length > 0);

    this.schema = schema;
    this.dynamicForm = this.formBuilder.group(controls);
    this.activePanelIds = this.groups.map((group) => group.panelId);
    this.formSubmitted = false;
    this.wireAmountsInWords();
  }

  /**
   * Repõe o comportamento do formulário antigo: escrever o valor numérico
   * preenche automaticamente o campo "por extenso", que ninguém quer digitar.
   */
  private wireAmountsInWords(): void {
    this.amountSubscriptions.unsubscribe();
    this.amountSubscriptions = new Subscription();

    AMOUNT_IN_WORDS_PAIRS.forEach(([amountKey, wordsKey]) => {
      const amount = this.controlFor(amountKey);
      const words = this.controlFor(wordsKey);
      if (!amount || !words) return;

      const apply = (value: unknown) => {
        const text = amountInWords(value);
        // Nunca por cima de texto escrito à mão pelo utilizador.
        if (words.dirty && words.value) return;
        words.setValue(text, { emitEvent: false });
      };
      if (!words.value) apply(amount.value);
      this.amountSubscriptions.add(amount.valueChanges.subscribe(apply));
    });
  }

  private controlFor(sourceKey: string): AbstractControl | null {
    for (const group of this.groups) {
      const field = group.fields.find((item) => item.sourceKey === sourceKey);
      if (field) return this.dynamicForm.get(field.controlName);
    }
    return null;
  }

  /** Quantos campos do grupo já têm valor — mostrado no cabeçalho da secção. */
  filledCount(group: DynamicCmiGroup): number {
    return group.fields.reduce((total, field) => {
      const value = this.dynamicForm.get(field.controlName)?.value;
      const filled = field.valueType === "CHECKBOX"
        ? value === true
        : value !== null && value !== undefined && String(value).trim() !== "";
      return total + (filled ? 1 : 0);
    }, 0);
  }

  private initialValue(field: DynamicCmiField): string | number | boolean | null {
    if (field.valueType === "CHECKBOX") {
      return field.value === true || field.value === "true";
    }
    if (field.value == null || field.value === "") return null;
    if (field.valueType === "DATE") return String(field.value).slice(0, 10);
    if (field.valueType === "CURRENCY" || field.valueType === "PERCENTAGE") {
      const number = Number(field.value);
      return Number.isFinite(number) ? number : null;
    }
    return String(field.value);
  }

  private formValues(): Record<string, string | number | boolean | null> {
    return this.groups.reduce<Record<string, string | number | boolean | null>>(
      (values, group) => {
        group.fields.forEach((field) => {
          const value = this.dynamicForm.get(field.controlName)?.value;
          values[field.sourceKey] = value == null || value === "" ? null : value;
        });
        return values;
      },
      {},
    );
  }

  private contextRequest(): CmiFormSchemaRequest {
    const request: CmiFormSchemaRequest = {};
    if (this.deal?.id != null) request.dealId = Number(this.deal.id);
    const propertyId = this.property?.id || this.deal?.property?.id;
    if (propertyId) request.propertyId = String(propertyId);
    return request;
  }

  private generationContext(): Pick<
    CmiFormSchemaRequest,
    "mediationAgreementId" | "dealId" | "propertyId"
  > {
    return {
      ...(this.schema?.mediationAgreementId
        ? { mediationAgreementId: this.schema.mediationAgreementId }
        : {}),
      ...this.contextRequest(),
    };
  }

  private openModal(): void {
    if (this.modalRef || !this.mediationContractModal) return;
    const modal = this.modalService.open(this.mediationContractModal, {
      size: "xl",
      centered: true,
      backdrop: "static",
      backdropClass: "modal-z",
      windowClass: "modal-z",
      scrollable: true,
    });
    this.modalRef = modal;
    modal.result.then(
      () => this.onModalFinished(modal),
      () => this.onModalFinished(modal),
    );
  }

  private onModalFinished(modal: NgbModalRef): void {
    if (this.modalRef === modal) this.modalRef = undefined;
    this.eventService.broadcast("contract-generated");
  }

  private async handleGenerationError(error: unknown): Promise<void> {
    const body = await this.readErrorBody(error);
    const code = body?.code ||
      (Array.isArray(body?.message) ? body?.message[0] : body?.message);
    // A ficha do imóvel não está completa (422): o servidor diz QUAIS os
    // campos — mostrá-los é o que permite corrigir.
    const missing = extractMissingFields(body);
    if (code === "PROPERTY_REQUIRED_FIELDS_INCOMPLETE" || missing.length) {
      await Swal.fire({
        title: this.translate.instant("t-listing-detail-validation-blocked"),
        html: missingFieldsHtml(missing, this.translate),
        icon: "warning",
        confirmButtonColor: "rgb(54, 69, 116)",
        confirmButtonText: this.translate.instant("t-close"),
      });
      return;
    }
    if ((error as { status?: number })?.status === 409 && code === "CMI_TEMPLATE_CHANGED") {
      await Swal.fire({
        title: this.translate.instant("t-cmi-templates.runtime.errors.template-changed-title"),
        text: this.translate.instant("t-cmi-templates.runtime.errors.template-changed"),
        icon: "warning",
        confirmButtonText: this.translate.instant("t-cmi-templates.ok"),
      });
      this.loadSchema();
      return;
    }
    await this.showRuntimeError(
      error,
      "t-cmi-templates.runtime.errors.generate",
      body,
    );
  }

  private async showRuntimeError(
    error: unknown,
    fallbackKey: string,
    parsedBody?: CmiErrorBody,
  ): Promise<void> {
    const body = parsedBody || (await this.readErrorBody(error));
    await Swal.fire({
      title: this.translate.instant("t-cmi-templates.runtime.errors.title"),
      text: this.translate.instant(body?.translationKey || fallbackKey),
      icon: "error",
      confirmButtonText: this.translate.instant("t-cmi-templates.ok"),
    });
  }

  private async readErrorBody(error: unknown): Promise<CmiErrorBody | undefined> {
    let body = (error as { error?: unknown })?.error;
    if (body instanceof Blob && body.type.includes("json")) {
      try {
        body = JSON.parse(await body.text());
      } catch {
        return undefined;
      }
    }
    return body && typeof body === "object" ? (body as CmiErrorBody) : undefined;
  }

  private downloadPdf(blob: Blob, fileName: string): void {
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = fileName;
    anchor.click();
    setTimeout(() => URL.revokeObjectURL(url));
  }

  private fileNameFromDisposition(
    disposition: string | null,
    fallback: string,
  ): string {
    if (!disposition) return fallback;
    const utf8 = disposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
    if (utf8) {
      try {
        return decodeURIComponent(utf8);
      } catch {
        return fallback;
      }
    }
    return disposition.match(/filename="?([^";]+)"?/i)?.[1] || fallback;
  }
}
